import React, { useEffect, useState, useCallback } from "react";
import styles from "./AdminGameAccounts.module.css";
import { getAccessToken } from "../../utils/authStorage";

// Use environment variable for the base URL
const ENV_API_URL =
  import.meta.env.VITE_API_URL || "https://pixelmoonstore.in/api/v1";
// Ensure we have the base API URL without version
const BASE_API_URL = ENV_API_URL.replace(/\/v1\/?$/, "");

async function api(path, method = "GET", body) {
  const token = getAccessToken();
  const headers = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const opts = {
    method,
    headers,
    credentials: "include",
  };
  if (body) opts.body = JSON.stringify(body);

  // Use the correct base URL for admin routes
  const res = await fetch(
    `${BASE_API_URL}/v1/admin/game-accounts${path}`,
    opts,
  );
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "API request failed");
  }
  return res.json();
}

export default function AdminGameAccounts() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterGame, setFilterGame] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api("", "GET");
      setList(res.accounts || []);
    } catch (e) {
      setError("Failed to load game accounts. Please try again.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (form) => {
      setLoading(true);
      setError(null);
      try {
        if (form._id) {
          await api(`/${form._id}`, "PUT", form);
          setSuccess("Account updated successfully!");
        } else {
          await api("", "POST", form);
          setSuccess("Account created successfully!");
        }
        setEditing(null);
        await load();
        setTimeout(() => setSuccess(null), 3000);
      } catch (e) {
        setError(e.message || "Failed to save the account.");
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [load],
  );

  const deleteAccount = useCallback(
    async (id, title) => {
      if (!window.confirm(`Are you sure you want to delete "${title}"?`))
        return;

      setLoading(true);
      setError(null);
      try {
        await api(`/${id}`, "DELETE");
        setSuccess("Account deleted successfully!");
        await load();
        setTimeout(() => setSuccess(null), 3000);
      } catch (e) {
        setError(e.message || "Failed to delete the account.");
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [load],
  );

  // Filter accounts based on search and game filter
  const filteredList = list.filter((account) => {
    const matchesSearch =
      account.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.game.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGame = !filterGame || account.game === filterGame;
    return matchesSearch && matchesGame;
  });

  const games = [...new Set(list.map((a) => a.game))].sort();

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Game Accounts Management</h1>
          <p className={styles.subtitle}>
            Add, edit, and manage game accounts for your marketplace
          </p>
        </div>
        <button
          className={styles.addButton}
          onClick={() => setEditing({})}
          disabled={loading}
        >
          <span className={styles.icon}>+</span> Add New Account
        </button>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>
      )}
      {success && (
        <div className={`${styles.alert} ${styles.alertSuccess}`}>
          {success}
        </div>
      )}

      {/* Search and Filter */}
      <div className={styles.filterSection}>
        <div className={styles.searchBox}>
          <input
            type="text"
            placeholder="Search by title or game name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <select
          value={filterGame}
          onChange={(e) => setFilterGame(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Games</option>
          {games.map((game) => (
            <option key={game} value={game}>
              {game}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {loading && (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading accounts...</p>
          </div>
        )}

        {!loading && filteredList.length === 0 && (
          <div className={styles.emptyState}>
            <p className={styles.emptyIcon}>📦</p>
            <p className={styles.emptyText}>
              {list.length === 0
                ? "No game accounts yet. Create your first one!"
                : "No accounts match your search."}
            </p>
          </div>
        )}

        {!loading && filteredList.length > 0 && (
          <div className={styles.accountsGrid}>
            {filteredList.map((account) => (
              <AccountCard
                key={account._id}
                account={account}
                onEdit={() => setEditing(account)}
                onDelete={() => deleteAccount(account._id, account.title)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {editing && (
        <AccountEditor
          form={editing}
          onClose={() => setEditing(null)}
          onSave={save}
          isLoading={loading}
        />
      )}
    </div>
  );
}

// Account Card Component
function AccountCard({ account, onEdit, onDelete }) {
  return (
    <div className={styles.accountCard}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{account.title}</h3>
        <span className={styles.badge}>{account.status || "available"}</span>
      </div>

      {account.images && account.images.length > 0 && (
        <div className={styles.cardImage}>
          <img src={account.images[0]} alt={account.title} />
        </div>
      )}

      <div className={styles.cardContent}>
        <p className={styles.cardGame}>
          <strong>Game:</strong> {account.game}
        </p>
        <p className={styles.cardCategory}>
          <strong>Category:</strong> {account.category || "N/A"}
        </p>
        <p className={styles.cardPrice}>
          <strong>Price:</strong> ₹{account.baseInrCost}
        </p>
        {account.description && (
          <p className={styles.cardDescription}>
            {account.description.substring(0, 80)}...
          </p>
        )}
      </div>

      <div className={styles.cardActions}>
        <button className={`${styles.btn} ${styles.btnEdit}`} onClick={onEdit}>
          Edit
        </button>
        <button
          className={`${styles.btn} ${styles.btnDelete}`}
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// Account Editor Modal Component
function AccountEditor({ form, onClose, onSave, isLoading }) {
  const [state, setState] = useState({ ...form });
  const [validationErrors, setValidationErrors] = useState({});

  const setField = (k, v) => {
    setState((s) => ({ ...s, [k]: v }));
    // Clear validation error for this field
    if (validationErrors[k]) {
      setValidationErrors((e) => ({ ...e, [k]: null }));
    }
  };

  const setOverride = (k, v) => {
    setState((s) => ({
      ...s,
      manualOverride: { ...s.manualOverride, [k]: v || null },
    }));
  };

  const validate = () => {
    const errors = {};
    if (!state.title?.trim()) errors.title = "Title is required";
    if (!state.game?.trim()) errors.game = "Game is required";
    if (!state.baseInrCost || state.baseInrCost <= 0)
      errors.baseInrCost = "Valid price is required";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(state);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {state._id ? "Edit Game Account" : "Create New Game Account"}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.formGrid}>
            {/* Left Column */}
            <div className={styles.formColumn}>
              <FormField
                label="Account Title *"
                value={state.title || ""}
                onChange={(e) => setField("title", e.target.value)}
                error={validationErrors.title}
                placeholder="e.g., Level 50 Premium Account"
              />

              <FormField
                label="Game Name *"
                value={state.game || ""}
                onChange={(e) => setField("game", e.target.value)}
                error={validationErrors.game}
                placeholder="e.g., PUBG, Call of Duty"
              />

              <FormField
                label="Category"
                value={state.category || ""}
                onChange={(e) => setField("category", e.target.value)}
                placeholder="e.g., Accounts, Top-ups, Skins"
              />

              <FormField
                label="Base INR Cost *"
                type="number"
                value={state.baseInrCost || ""}
                onChange={(e) =>
                  setField("baseInrCost", Number(e.target.value))
                }
                error={validationErrors.baseInrCost}
                placeholder="0"
              />

              <FormField
                label="Images (comma-separated URLs)"
                value={(state.images || []).join(", ")}
                onChange={(e) =>
                  setField(
                    "images",
                    e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
                placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                isTextarea
                rows={3}
              />
            </div>

            {/* Right Column */}
            <div className={styles.formColumn}>
              <FormField
                label="Description"
                value={state.description || ""}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Detailed description of the account or product"
                isTextarea
                rows={6}
              />

              <div className={styles.priceOverridesSection}>
                <h3 className={styles.overridesTitle}>Price Overrides (INR)</h3>
                <p className={styles.overridesSubtitle}>
                  Leave empty to use base price
                </p>

                <div className={styles.overridesGrid}>
                  {[
                    { key: "reseller", label: "Reseller Price" },
                    { key: "customer", label: "Customer Price" },
                    { key: "vip1", label: "VIP1 Price" },
                    { key: "vip2", label: "VIP2 Price" },
                    { key: "vip3", label: "VIP3 Price" },
                  ].map(({ key, label }) => (
                    <div key={key} className={styles.overrideField}>
                      <label className={styles.overrideLabel}>{label}</label>
                      <input
                        type="number"
                        className={styles.overrideInput}
                        defaultValue={state.manualOverride?.[key] ?? ""}
                        onBlur={(e) =>
                          setOverride(key, Number(e.target.value) || null)
                        }
                        placeholder={`₹`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnCancel}`}
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className={`${styles.btn} ${styles.btnSave}`}
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Account"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reusable Form Field Component
function FormField({
  label,
  value,
  onChange,
  error,
  placeholder,
  type = "text",
  isTextarea = false,
  rows = 1,
}) {
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>{label}</label>
      {isTextarea ? (
        <textarea
          className={`${styles.formInput} ${error ? styles.inputError : ""}`}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
        />
      ) : (
        <input
          type={type}
          className={`${styles.formInput} ${error ? styles.inputError : ""}`}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      )}
      {error && <span className={styles.fieldError}>{error}</span>}
    </div>
  );
}
